/**
 * The tab's live sockets: at most eight, one per World in view.
 *
 * The focused World, the hands of an open Loom and a few recently viewed
 * Worlds each keep a link; opening a ninth closes the one opened longest
 * ago. Every link feeds one Roster, so the HUD and the field read presence
 * from one place. The pool also carries the browser's signals to every
 * link: back online means try now, a hidden tab pauses presence and
 * re-upgrades, a visible one resumes them.
 */

import type { CreateLinks, Links, SessionApi, Client, Unsub } from '../contracts';
import { createLink, type LiveLink } from './link';
import { createRoster, type RosterFeed, type Timers } from './presence';

export const MAX_LINKS = 8;
/** How often the roster is swept for peers whose `left` was lost. */
export const SWEEP_MS = 15_000;

export interface LinksDeps {
  readonly session: SessionApi;
  readonly client: Client;
  readonly socket?: (url: string) => WebSocket;
  readonly fetchFn?: typeof fetch;
  readonly now?: () => number;
  readonly timers?: Timers;
  readonly random?: () => number;
  readonly max?: number;
  /** The tab's visibility; the document's when there is one. */
  readonly visibility?: { hidden(): boolean; on(fn: (hidden: boolean) => void): Unsub };
  /** The browser's `online` event; the window's when there is one. */
  readonly online?: (fn: () => void) => Unsub;
}

export interface LivePool extends Links {
  open(world: string, opts: { presence: boolean }): LiveLink;
  get(world: string): LiveLink | undefined;
  /** The Worlds with a link, most recently opened last. */
  worlds(): readonly string[];
}

const documentVisibility = (): LinksDeps['visibility'] => {
  const doc = (globalThis as { document?: Document }).document;
  if (!doc || typeof doc.addEventListener !== 'function') return undefined;
  return {
    hidden: () => doc.visibilityState === 'hidden',
    on(fn) {
      const handler = () => fn(doc.visibilityState === 'hidden');
      doc.addEventListener('visibilitychange', handler);
      return () => doc.removeEventListener('visibilitychange', handler);
    },
  };
};

const windowOnline = (): LinksDeps['online'] => {
  const win = globalThis as { addEventListener?: (t: string, fn: () => void) => void; removeEventListener?: (t: string, fn: () => void) => void; document?: unknown };
  if (!win.document || typeof win.addEventListener !== 'function') return undefined;
  return (fn) => {
    win.addEventListener!('online', fn);
    return () => win.removeEventListener?.('online', fn);
  };
};

export const createLinks: CreateLinks = (deps) => makeLinks(deps);

export function makeLinks(deps: LinksDeps): { links: LivePool; roster: RosterFeed } {
  const now = deps.now ?? Date.now;
  const timers: Timers = deps.timers ?? { set: (fn, ms) => setTimeout(fn, ms), clear: (id) => clearTimeout(id as ReturnType<typeof setTimeout>) };
  const random = deps.random ?? Math.random;
  const max = Math.max(1, deps.max ?? MAX_LINKS);
  const socket = deps.socket ?? ((url: string) => new WebSocket(url));
  const visibility = deps.visibility ?? documentVisibility();
  const online = deps.online ?? windowOnline();
  const roster = createRoster({ now });

  /** Links in the order they were last opened, oldest first. */
  const pool = new Map<string, { link: LiveLink; unsubs: Unsub[] }>();
  let sweeper: unknown = null;
  let hooks: Unsub[] = [];

  const arm = () => {
    if (sweeper !== null) return;
    sweeper = timers.set(() => {
      sweeper = null;
      roster.sweep();
      if (pool.size) arm();
    }, SWEEP_MS);
    if (hooks.length === 0) {
      if (visibility) hooks.push(visibility.on((hidden) => { for (const { link } of pool.values()) link.setHidden(hidden); }));
      if (online) hooks.push(online(() => { for (const { link } of pool.values()) link.retryNow(); }));
    }
  };

  const drop = (world: string) => {
    const entry = pool.get(world);
    if (!entry) return;
    pool.delete(world);
    for (const u of entry.unsubs) u();
    entry.link.close();
    roster.drop(world);
  };

  const links: LivePool = {
    open(world, opts) {
      const had = pool.get(world);
      if (had && had.link.state() !== 'closed' && (had.link.presence || !opts.presence)) {
        pool.delete(world);
        pool.set(world, had);
        return had.link;
      }
      if (had) drop(world);
      const link = createLink(world, opts, {
        apiBase: deps.session.apiBase, client: deps.client, profile: () => deps.session.profile(),
        socket, now, timers, random, hidden: () => visibility?.hidden() ?? false,
      });
      const unsubs = [
        link.onPresence((f) => roster.apply(world, f)),
        // A link closed by hand, or finished by the API (401, 404), is no longer watched: its peers and its place go.
        link.onState((state) => { if (state === 'closed' && pool.get(world)?.link === link) drop(world); }),
      ];
      pool.set(world, { link, unsubs });
      while (pool.size > max) {
        const oldest = pool.keys().next().value;
        if (oldest === undefined) break;
        drop(oldest);
      }
      arm();
      return link;
    },
    get: (world) => pool.get(world)?.link,
    worlds: () => [...pool.keys()],
    closeAll() {
      for (const world of [...pool.keys()]) drop(world);
      if (sweeper !== null) timers.clear(sweeper);
      sweeper = null;
      for (const u of hooks) u();
      hooks = [];
    },
  };
  return { links, roster };
}
